import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    getEmailAccountsService,
    getEmailAccountService,
    deleteEmailAccountService,
    testEmailAccountService,
    syncEmailAccountService,
    setDefaultEmailAccountService,
    getGmailOAuthUrlService,
    gmailOAuthCallbackService,
    sendEmailService
} from "../services/email-account-service";

export function useGetEmailAccounts() {
    return useQuery({
        queryKey: ['email-accounts'],
        queryFn: getEmailAccountsService,
    });
}

export function useGetEmailAccount(id: string) {
    return useQuery({
        queryKey: ['email-account', id],
        queryFn: () => getEmailAccountService(id),
        enabled: !!id,
    });
}

export function useDeleteEmailAccount() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['delete-email-account'],
        mutationFn: deleteEmailAccountService,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
        }
    });
}

export function useTestEmailAccount() {
    return useMutation({
        mutationKey: ['test-email-account'],
        mutationFn: testEmailAccountService,
    });
}

export function useSyncEmailAccount() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['sync-email-account'],
        mutationFn: syncEmailAccountService,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
        }
    });
}

export function useSetDefaultEmailAccount() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['set-default-email-account'],
        mutationFn: setDefaultEmailAccountService,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
        }
    });
}

export function useGmailCallback() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ code, state }: { code: string, state: string }) => gmailOAuthCallbackService(code, state),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
        }
    });
}

export function useGetGmailOAuthUrl() {
    return useQuery({
        queryKey: ['gmail-oauth-url'],
        queryFn: getGmailOAuthUrlService,
        enabled: false, // Don't run automatically
    });
}

export function useGmailOAuthCallback() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['gmail-oauth-callback'],
        mutationFn: ({ code, state }: { code: string, state: string }) => gmailOAuthCallbackService(code, state),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
        }
    });
}

export function useSendEmail() {
    return useMutation({
        mutationKey: ['send-email'],
        mutationFn: sendEmailService,
    });
}

